import {
  Box, Typography, Grid, Stack, TextField, Button,
  Divider, MenuItem, Select, FormControl, Chip, Alert,
  Avatar, IconButton, Tooltip, CircularProgress,
} from "@mui/material";
import React, { useContext, useState, useEffect, useRef } from "react";
import { UserDetailsContext } from "../../Context/userDetailsContext";
import { useForm, Controller } from "react-hook-form";
import { isMobile } from "react-device-detect";
import axios from "../../axiosConfig";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { resolveMediaUrl } from "../../utils/mediaUrl";

// ── helper ────────────────────────────────────────────────────────────────────
const InfoCell = ({ label, value }) => (
  <Box sx={{ px: 2, py: 1.75, textAlign: "center" }}>
    <Typography sx={{ fontSize: 12, color: "#666", mb: 0.5, letterSpacing: 0.2, fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#222" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const FieldLabel = ({ children }) => (
  <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#555", mb: 0.75 }}>
    {children}
  </Typography>
);

// shared sx for editable/disabled text fields
const fieldSx = (editing) => ({
  "& .MuiInputBase-root": { backgroundColor: editing ? "#fff" : "#F5F5F5" },
  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#333", opacity: 1 },
});

// ── component ─────────────────────────────────────────────────────────────────
const PersonalInfo = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const { userDetails, setUserDetails } = useContext(UserDetailsContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith("ar");

  const userInfo = userDetails?.citizen || {};

  const { handleSubmit, control, reset, clearErrors, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: {
      phone1: userInfo.phone1 || "",
      phone2: userInfo.phone2 || "",
      telephone: userInfo.telephone || "",
      email: userInfo.email || "",
      preferredChannel: userInfo.preferredChannel?.toString() || "",
      address: userInfo.address || "",
    },
  });

  // Reset form when user data loads
  useEffect(() => {
    if (userInfo) {
      reset({
        phone1: userInfo.phone1 || "",
        phone2: userInfo.phone2 || "",
        telephone: userInfo.telephone || "",
        email: userInfo.email || "",
        preferredChannel: userInfo.preferredChannel?.toString() || "",
        address: userInfo.address || "",
      });
    }
  }, [userInfo.phone1, userInfo.email]);

  // Load notification types
  useEffect(() => {
    axios.get("/lookup/notificationTypes")
      .then((res) => {
        const data = Array.isArray(res) ? res : (res?.dataList || res?.data || []);
        setNotificationTypes(data);
      })
      .catch(() => setNotificationTypes([]));
  }, []);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      setErrorMsg(isRTL ? "نوع الصورة غير مدعوم. المسموح: JPEG, PNG" : "Unsupported image type");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMsg(isRTL ? "حجم الصورة يتجاوز 5 ميغابايت" : "Image exceeds 5 MB");
      return;
    }
    try {
      setIsUploadingPhoto(true);
      setErrorMsg("");
      const formData = new FormData();
      formData.append("file", file);
      const picPath = await axios.put("user/ProfilePicture", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const preview = URL.createObjectURL(file);
      setUserDetails({
        ...userDetails,
        citizen: { ...userDetails.citizen, profilePicture: picPath, profilePicturePreview: preview },
      });
      setSuccessMsg(isRTL ? "تم تحديث الصورة بنجاح ✓" : "Photo updated successfully ✓");
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      setErrorMsg(isRTL ? "فشل رفع الصورة" : "Failed to upload photo");
    } finally {
      setIsUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const cancelEditing = () => {
    reset({
      phone1: userInfo.phone1 || "",
      phone2: userInfo.phone2 || "",
      telephone: userInfo.telephone || "",
      email: userInfo.email || "",
      preferredChannel: userInfo.preferredChannel?.toString() || "",
      address: userInfo.address || "",
    });
    clearErrors();
    setIsEditing(false);
    setErrorMsg("");
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString(isRTL ? "ar-SA" : "en-US");
  };

  const getChannelLabel = (id) => {
    const ch = notificationTypes.find((n) => n.id === parseInt(id));
    return ch?.name || "—";
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setErrorMsg("");
      const payload = {
        phone1: data.phone1,
        email: data.email?.trim() || "",
        preferredChannel: parseInt(data.preferredChannel),
        ...(data.phone2?.trim() && { phone2: data.phone2.trim() }),
        ...(data.telephone?.trim() && { telephone: data.telephone.trim() }),
        ...(data.address?.trim() && { address: data.address.trim() }),
      };
      const response = await axios.put("user", payload);
      setIsLoading(false);
      setIsEditing(false);
      setSuccessMsg(isRTL ? "تم تحديث البيانات بنجاح ✓" : "Data updated successfully ✓");
      setTimeout(() => setSuccessMsg(""), 4000);
      setUserDetails({ ...userDetails, citizen: { ...userInfo, ...payload } });
    } catch (error) {
      setIsLoading(false);
      const msg = error?.message || (isRTL ? "فشل في تحديث البيانات" : "Failed to update");
      setErrorMsg(msg);
    }
  };

  // ── section card wrapper ───────────────────────────────────────────────────
  const SectionCard = ({ icon, title, children }) => (
    <Box
      sx={{
        border: "1px solid #E8ECF0",
        borderRadius: 3,
        mb: 3,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2.5,
          py: 1.5,
          bgcolor: "#FAFBFC",
          borderBottom: "1px solid #E8ECF0",
        }}
      >
        {icon}
        <Typography variant="subtitle1" fontWeight={700} color="primary.main">
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 2.5, py: 1.5 }}>{children}</Box>
    </Box>
  );

  const avatarSrc = userDetails?.citizen?.profilePicturePreview
    || resolveMediaUrl(userDetails?.citizen?.profilePicture, { attachmentRoot: true });

  return (
    <Box dir={isRTL ? "rtl" : "ltr"}>
      {successMsg && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{successMsg}</Alert>}
      {errorMsg   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }}>{errorMsg}</Alert>}

      {/* ── Profile Picture ── */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <Avatar
            src={avatarSrc}
            sx={{ width: 100, height: 100, fontSize: "2.5rem", boxShadow: 3 }}
          >
            {!avatarSrc && (userDetails?.citizen?.firstName?.[0] || "?")}
          </Avatar>
          <Tooltip title={isRTL ? "تغيير الصورة" : "Change photo"}>
            <IconButton
              onClick={() => photoInputRef.current?.click()}
              disabled={isUploadingPhoto}
              size="small"
              sx={{
                position: "absolute", bottom: 0, right: 0,
                bgcolor: "primary.main", color: "white",
                width: 30, height: 30,
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              {isUploadingPhoto
                ? <CircularProgress size={14} color="inherit" />
                : <PhotoCameraIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png"
            style={{ display: "none" }}
            onChange={handlePhotoChange}
          />
        </Box>
      </Box>

      {/* ── SECTION 1: Read-only personal info ── */}
      <SectionCard
        icon={<AccountCircleOutlinedIcon color="primary" />}
        title={isRTL ? "المعلومات الشخصية" : "Personal Information"}
      >
        {/* negative margin to let grid touch card edges */}
        <Box sx={{ mx: -2.5, mb: -1.5, mt: -1.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
              "& > *": {
                borderBottom: "1px solid #F4F4F4",
                borderInlineEnd: "1px solid #F4F4F4",
              },
              // xs: 2 cols — remove end border on even items, bottom border on last 2
              "@media (max-width: 899px)": {
                "& > *:nth-of-type(2n)": { borderInlineEnd: "none" },
                "& > *:nth-last-of-type(-n+2)": { borderBottom: "none" },
              },
              // md: 4 cols — remove end border on every 4th, bottom border on last 4
              "@media (min-width: 900px)": {
                "& > *:nth-of-type(4n)": { borderInlineEnd: "none" },
                "& > *:nth-last-of-type(-n+4)": { borderBottom: "none" },
              },
            }}
          >
            <InfoCell label={isRTL ? "الاسم الكامل"    : "Full Name"}          value={userInfo.fullName} />
            <InfoCell label={isRTL ? "نوع الوثيقة"     : "Document Type"}      value={userInfo.documentType?.name} />
            <InfoCell label={isRTL ? "رقم الوثيقة"     : "Document Number"}    value={userInfo.documentNumber} />
            <InfoCell label={isRTL ? "تاريخ الميلاد"   : "Birth Date"}         value={formatDate(userInfo.birthdate)} />
            <InfoCell label={isRTL ? "الجنس"           : "Gender"}             value={userInfo.gender?.name} />
            <InfoCell label={isRTL ? "مكان الإقامة"    : "Place of Residence"} value={userInfo.country?.name} />
            <InfoCell label={isRTL ? "المدينة"         : "City"}               value={userInfo.city} />
            <InfoCell label={isRTL ? "صندوق البريد"    : "Mail Box"}           value={userInfo.mailBox} />
          </Box>
        </Box>
      </SectionCard>

      {/* ── SECTION 2: Editable contact info ── */}
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <SectionCard
          icon={<ContactPhoneOutlinedIcon color="primary" />}
          title={isRTL ? "معلومات الاتصال" : "Contact Information"}
        >
          <Grid container spacing={2}>

            {/* Telephone */}
            <Grid item xs={12} md={6}>
              <FieldLabel>{isRTL ? "الهاتف الأرضي" : "Telephone"}</FieldLabel>
              <Controller
                name="telephone"
                control={control}
                rules={{ pattern: { value: /^[0-9]*$/, message: isRTL ? "أرقام فقط" : "Numbers only" } }}
                render={({ field }) => (
                  <TextField {...field} fullWidth disabled={!isEditing} size="small"
                    inputProps={{ inputMode: "numeric" }}
                    error={!!errors.telephone} helperText={errors.telephone?.message}
                    placeholder={isRTL ? "أدخل الهاتف الأرضي" : "Enter telephone"}
                    sx={fieldSx(isEditing)}
                  />
                )}
              />
            </Grid>

            {/* Phone 1 */}
            <Grid item xs={12} md={6}>
              <FieldLabel>{isRTL ? "رقم الموبايل 1 *" : "Mobile Number 1 *"}</FieldLabel>
              <Controller
                name="phone1"
                control={control}
                rules={{
                  required: isRTL ? "هذا الحقل مطلوب" : "Required",
                  pattern: { value: /^[0-9]*$/, message: isRTL ? "أرقام فقط" : "Numbers only" },
                }}
                render={({ field }) => (
                  <TextField {...field} fullWidth disabled={!isEditing} size="small"
                    inputProps={{ inputMode: "numeric" }}
                    error={!!errors.phone1} helperText={errors.phone1?.message}
                    placeholder={isRTL ? "أدخل رقم الموبايل" : "Enter mobile number"}
                    sx={fieldSx(isEditing)}
                  />
                )}
              />
            </Grid>

            {/* Phone 2 */}
            <Grid item xs={12} md={6}>
              <FieldLabel>{isRTL ? "رقم الموبايل 2" : "Mobile Number 2"}</FieldLabel>
              <Controller
                name="phone2"
                control={control}
                rules={{ pattern: { value: /^[0-9]*$/, message: isRTL ? "أرقام فقط" : "Numbers only" } }}
                render={({ field }) => (
                  <TextField {...field} fullWidth disabled={!isEditing} size="small"
                    inputProps={{ inputMode: "numeric" }}
                    error={!!errors.phone2} helperText={errors.phone2?.message}
                    placeholder={isRTL ? "أدخل رقم الموبايل 2" : "Enter mobile 2"}
                    sx={fieldSx(isEditing)}
                  />
                )}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <FieldLabel>{isRTL ? "البريد الإلكتروني" : "Email"}</FieldLabel>
              <Controller
                name="email"
                control={control}
                rules={{
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: isRTL ? "بريد إلكتروني غير صحيح" : "Invalid email",
                  },
                }}
                render={({ field }) => (
                  <TextField {...field} fullWidth disabled={!isEditing} size="small"
                    type="email" autoComplete="off"
                    error={!!errors.email} helperText={errors.email?.message}
                    placeholder={isRTL ? "أدخل البريد الإلكتروني" : "Enter email"}
                    sx={fieldSx(isEditing)}
                  />
                )}
              />
            </Grid>

            {/* Preferred Channel */}
            <Grid item xs={12} md={6}>
              <FieldLabel>{isRTL ? "وسيلة التواصل المفضلة" : "Preferred Notification"}</FieldLabel>
              <Controller
                name="preferredChannel"
                control={control}
                render={({ field }) => (
                  isEditing ? (
                    <FormControl fullWidth size="small">
                      <Select {...field} displayEmpty sx={{ backgroundColor: "#fff" }}>
                        <MenuItem value=""><em>{isRTL ? "اختر وسيلة التواصل" : "Select"}</em></MenuItem>
                        {notificationTypes.map((nt) => (
                          <MenuItem key={nt.id} value={nt.id.toString()}>{nt.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField fullWidth disabled size="small"
                      value={getChannelLabel(field.value)}
                      sx={fieldSx(false)}
                    />
                  )
                )}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <FieldLabel>{isRTL ? "العنوان" : "Address"}</FieldLabel>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth disabled={!isEditing} size="small"
                    multiline minRows={2}
                    placeholder={isRTL ? "أدخل العنوان" : "Enter address"}
                    sx={fieldSx(isEditing)}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            {!isEditing ? (
              <Button variant="contained" sx={{ minWidth: 110 }} onClick={() => setIsEditing(true)}>
                {isRTL ? "تعديل" : "Edit"}
              </Button>
            ) : (
              <>
                <Button variant="outlined" color="inherit" sx={{ minWidth: 110 }} onClick={cancelEditing}>
                  {isRTL ? "إلغاء" : "Cancel"}
                </Button>
                <LoadingButton type="submit" variant="contained" loading={isLoading} sx={{ minWidth: 110 }}>
                  {isRTL ? "حفظ" : "Save"}
                </LoadingButton>
              </>
            )}
          </Box>
        </SectionCard>
      </form>
    </Box>
  );
};

export default PersonalInfo;
